import { createSseEventBus } from '@webapp/core/services/EventBus'
import { EventBusConnectionStates } from '@webapp/infrastructure/interfaces/services/IEventBus'
import {
  type IConversationRepository,
  type ConversationStreamHandlers,
} from '../../business-logic/interfaces/IConversationRepository'
import {
  ConversationStreamEventTypes,
  type ConversationEventMap,
} from '../api-models/ConversationEventApiModel'
import {
  mapConversationEnvelopeEvent,
  mapConversationTelemetryEvent,
} from '../mappers/mapConversationEventApiModel'
import { type ConversationTurnPayload } from '../types/ConversationTurnPayload'

const AGENT_ENDPOINT = '/api/v3/global-command/agent/'

export class ConversationRepository implements IConversationRepository {
  openTurn(
    payload: ConversationTurnPayload,
    handlers: ConversationStreamHandlers
  ): () => void {
    const bus = createSseEventBus<ConversationEventMap>({
      url: AGENT_ENDPOINT,
      body: {
        message: payload.message,
        ...(payload.conversationId !== null && {
          conversation_id: payload.conversationId,
        }),
      },
      headers: { Accept: 'text/event-stream, */*' },
    })

    let isDone = false
    const finish = (): void => {
      if (isDone) {
        return
      }
      isDone = true
      handlers.onDone()
    }

    let hasStarted = false
    const startSession = (conversationId: string): void => {
      if (hasStarted) {
        return
      }
      hasStarted = true
      handlers.onSessionStart(conversationId)
    }

    bus.bind('message', (event) => {
      if (isDone) {
        return
      }

      handlers.onTelemetry(mapConversationTelemetryEvent(event))

      switch (event.type) {
        case ConversationStreamEventTypes.CONVERSATION_ASSIGNED:
        case ConversationStreamEventTypes.SESSION_START:
          startSession(event.data.conversation_id)
          break
        case ConversationStreamEventTypes.THINKING:
          handlers.onProgress(event.data.label)
          break
        case ConversationStreamEventTypes.UI_UPDATE:
          handlers.onItem(mapConversationEnvelopeEvent(event.data.envelope))
          break
        case ConversationStreamEventTypes.MESSAGE_FINAL:
          handlers.onItem(mapConversationEnvelopeEvent(event.data.envelope))
          finish()
          break
        case ConversationStreamEventTypes.OUT_OF_SCOPE:
          finish()
          break
        case ConversationStreamEventTypes.ERROR:
          handlers.onError({
            code: event.data.code,
            message: event.data.message,
          })
          finish()
          break
        default:
          break
      }
    })

    bus.bind('state', ({ state }) => {
      if (state === EventBusConnectionStates.DISCONNECTED) {
        finish()
      }
    })

    bus.bind('error', (error) => {
      handlers.onError({ code: error.code, message: error.message })
      finish()
    })

    bus.connect()

    return () => {
      bus.disconnect()
      finish()
    }
  }
}
